import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type DriverExpense } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column, type BadgeTone } from '@/components/ui';
import { IconReceipt } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';
import { useAdminPermission } from '@/lib/useAdminPermission';

const PAGE = 25;

// Expense status → badge tone (0 Pending / 1 Approved / 2 Rejected). Matches
// admin-svc's expenseStatusName + migration 026.
const STATUS_TONE: Record<number, BadgeTone> = { 0: 'info', 1: 'ok', 2: 'danger' };

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: '0', label: 'Pending' },
  { value: '1', label: 'Approved' },
  { value: '2', label: 'Rejected' },
];

/** Compact AUD formatter ($ + 2dp). cost is plain dollars (not km-credit). */
function aud(n: number): string {
  return `$${(n ?? 0).toFixed(2)}`;
}

function statusBadge(e: DriverExpense) {
  return <Badge tone={STATUS_TONE[e.status] ?? 'neutral'}>{e.status_name || String(e.status)}</Badge>;
}

/** DriverExpenses — reimbursement reports drivers file from the app (title +
 *  reason + cost in AUD + an S3 receipt). earnings-svc OWNS driver_expenses;
 *  admin-svc reads it through the shared pool and flips the status. Open a row
 *  to view the receipt and approve/reject (gated by expenses.manage — ops +
 *  finance). DEFENSIVE: a clean "not yet in Postgres" state when the table is
 *  absent. */
export function DriverExpenses() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<DriverExpense | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'driver-expenses', q, status, offset],
    queryFn: ({ signal }) => adminApi.driverExpenses({ q, status: status || undefined, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const columns: Array<Column<DriverExpense>> = [
    { key: 'status', header: 'Status', cell: (r) => statusBadge(r) },
    {
      key: 'driver',
      header: 'Driver',
      cell: (r) => (
        <div className="min-w-0">
          <div className="truncate text-sm text-slate-100">{r.driver_name}</div>
          <div className="metric text-[11px] text-muted">#{r.driver_id}</div>
        </div>
      ),
    },
    { key: 'title', header: 'Expense', cell: (r) => <span className="truncate text-sm text-slate-200">{r.title || '—'}</span> },
    {
      key: 'cost',
      header: 'Cost',
      align: 'right',
      cell: (r) => <span className="metric text-sm text-slate-100">{aud(r.cost)}</span>,
    },
    { key: 'when', header: 'Requested', cell: (r) => <span className="text-xs text-muted">{dateTime(r.created_at)}</span> },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          View
        </button>
      ),
    },
  ];

  const unavailable = data && data.available === false;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Driver Expenses"
        subtitle="Expense reimbursement reports drivers file from the app. Open one to view the receipt and approve or reject."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search by driver / title / reason…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Driver expenses not yet in Postgres"
            description={data?.note ?? 'The driver_expenses table is owned by earnings-svc and is not in this Postgres yet. This page lights up automatically once it lands.'}
            icon={<IconReceipt className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {STATUS_FILTERS.map((f) => {
              const active = f.value === status;
              return (
                <button
                  key={f.value || 'all'}
                  type="button"
                  onClick={() => {
                    setStatus(f.value);
                    setOffset(0);
                  }}
                  className={
                    active
                      ? 'rounded-md bg-brand-500/20 px-2.5 py-1 text-xs font-medium text-brand-300 ring-1 ring-inset ring-brand-500/40'
                      : 'rounded-md bg-ink-850 px-2.5 py-1 text-xs font-medium text-slate-300 ring-1 ring-inset ring-ink-700 hover:bg-ink-800'
                  }
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading expenses…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load expenses" description={(error as Error)?.message} />
            ) : (
              <>
                <DataTable<DriverExpense>
                  columns={columns}
                  rows={data?.items ?? []}
                  rowKey={(r) => String(r.id)}
                  stickyHeader
                  empty={
                    <EmptyState title="No expenses" description="No driver expenses match this filter." icon={<IconReceipt className="h-6 w-6" />} />
                  }
                />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>
        </>
      )}

      {selected ? <ExpenseDrawer expense={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

/** ExpenseDrawer — full report detail + the receipt + approve/reject controls
 *  (gated by expenses.manage). The list row carries every field, so no extra
 *  fetch is needed for the body. */
function ExpenseDrawer({ expense: e, onClose }: { expense: DriverExpense; onClose: () => void }) {
  const canManage = useAdminPermission('expenses.manage');
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (next: 0 | 1 | 2) => adminApi.setExpenseStatus(e.id, next),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'driver-expenses'] });
      onClose();
    },
  });
  const isAbsolute = /^https?:\/\//.test(e.evidence_url || '');

  return (
    <Drawer title={`Expense #${e.id}`} subtitle={`${e.driver_name} · ${e.status_name}`} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <Field label="Status" value={statusBadge(e)} />
          <Field label="Driver" value={`${e.driver_name} (#${e.driver_id})`} />
          {e.driver_phone ? <Field label="Phone" value={<span className="metric">{e.driver_phone}</span>} /> : null}
          {e.driver_email ? <Field label="Email" value={e.driver_email} /> : null}
          <Field label="Title" value={e.title || '—'} />
          <Field label="Reason" value={e.reason || '—'} />
          <Field label="Cost" value={<span className="metric text-slate-100">{aud(e.cost)}</span>} />
          <Field label="Requested" value={dateTime(e.created_at)} />
        </div>

        {e.evidence_url ? (
          isAbsolute ? (
            <a href={e.evidence_url} target="_blank" rel="noreferrer" className="inline-block text-xs text-brand-400 hover:underline">
              View receipt ↗
            </a>
          ) : (
            <p className="break-all text-xs text-muted">Receipt: {e.evidence_url}</p>
          )
        ) : (
          <p className="text-xs text-muted">No receipt attached.</p>
        )}

        {canManage ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-ink-800 pt-4">
            <button
              type="button"
              disabled={mutation.isPending || e.status === 1}
              onClick={() => mutation.mutate(1)}
              className="rounded-md bg-ok/15 px-3 py-1.5 text-xs font-medium text-ok ring-1 ring-inset ring-ok/30 hover:bg-ok/25 disabled:opacity-50"
            >
              Approve
            </button>
            <button
              type="button"
              disabled={mutation.isPending || e.status === 2}
              onClick={() => mutation.mutate(2)}
              className="rounded-md bg-danger/15 px-3 py-1.5 text-xs font-medium text-danger ring-1 ring-inset ring-danger/30 hover:bg-danger/25 disabled:opacity-50"
            >
              Reject
            </button>
            {e.status !== 0 ? (
              <button
                type="button"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate(0)}
                className="rounded-md border border-ink-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-ink-800 disabled:opacity-50"
              >
                Re-open
              </button>
            ) : null}
            {mutation.isError ? <span className="text-xs text-danger">{(mutation.error as Error)?.message}</span> : null}
          </div>
        ) : (
          <p className="border-t border-ink-800 pt-4 text-xs text-muted">
            Read-only — approving/rejecting reimbursements requires the ops or finance role.
          </p>
        )}
      </div>
    </Drawer>
  );
}
